import React, { useEffect, useState } from 'react';
import ModalLayout from '../ModalLayout';
import { setTriggers } from '../../../redux/device';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/solid';
import {
  createTrigger,
  updateTrigger,
  deleteTrigger,
  fetchTriggers,
} from '../../../api/trigger';
import { useDispatch } from 'react-redux';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';

const DeviceTrigger = ({ triggers, closeModal, deviceId }) => {
  const initialElements = triggers
    ? triggers.map((trigger) => ({
        ...trigger,
        messageTemplate: trigger.message_template,
      }))
    : [];

  const [elements, setElements] = useState(initialElements);
  const [type, setType] = useState('TG');
  const [condition, setCondition] = useState('');
  const [messageTemplate, setMessageTemplate] = useState('');
  const [timeout, setTimeout] = useState('5');
  const [resetAfter, setResetAfter] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    async function fetchData() {
      try {
        const triggers = await fetchTriggers(deviceId);
        dispatch(setTriggers(triggers));
      } catch (error) {
        console.error('Failed to fetch triggers:', error);
      }
    }
    if (loaded) {
      fetchData();
    }
    setLoaded(true);
  }, [elements, deviceId, dispatch, loaded]);

  const handleAddElement = async () => {
    if (!condition || !messageTemplate) {
      alert('Заполните все поля.');
      return;
    }

    try {
      if (editingIndex !== null) {
        const updatedTrigger = await updateTrigger(
          deviceId,
          elements[editingIndex].id,
          type,
          condition,
          messageTemplate,
          timeout,
          resetAfter
        );
        const updatedElements = [...elements];
        updatedElements[editingIndex] = {
          ...updatedTrigger,
          messageTemplate: updatedTrigger.message_template,
        };
        setElements(updatedElements);
        setEditingIndex(null);
      } else {
        const createdTrigger = await createTrigger(
          deviceId,
          type,
          condition,
          messageTemplate,
          timeout,
          resetAfter
        );
        setElements([
          ...elements,
          {
            ...createdTrigger,
            messageTemplate: createdTrigger.message_template,
          },
        ]);
      }
      resetForm();
    } catch (error) {
      console.error('Failed to save trigger:', error);
      alert('Failed to save trigger. Please try again.');
    }
  };

  const handleEditElement = (index) => {
    const element = elements[index];
    setType(element.type);
    setCondition(element.condition);
    setMessageTemplate(element.messageTemplate);
    setTimeout(element.timeout);
    setResetAfter(element.resetAfter);
    setEditingIndex(index);
  };

  const handleDeleteElement = async (index) => {
    try {
      const triggerId = elements[index].id;
      await deleteTrigger(deviceId, triggerId);
      const updatedElements = elements.filter((_, i) => i !== index);
      setElements(updatedElements);
    } catch (error) {
      console.error('Failed to delete trigger:', error);
      alert('Failed to delete trigger. Please try again.');
    }
  };

  const resetForm = () => {
    setType('TG');
    setCondition('');
    setMessageTemplate('');
    setTimeout('5');
    setResetAfter(false);
    setEditingIndex(null);
  };

  const handleModalClick = (e) => {
    e.stopPropagation();
  };

  return (
    <ModalLayout closeModal={closeModal}>
      <div className="p-4 space-y-6" onClick={handleModalClick}>
        {/* Header Section */}
        <div className="flex items-center justify-between pb-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Конфигурация нотификаций
          </h2>
          <a
            className="text-sm font-medium text-blue-600 hover:text-blue-800"
            target="_blank"
            href="/docs"
            rel="noopener noreferrer"
          >
            Подробнее
          </a>
        </div>

        {/* Table Section */}
        <div className="bg-white border border-gray-200">
          <div className="space-y-1">
            <div className="bg-gray-50 p-3 border-b border-gray-200">
              <div className="grid grid-cols-[1fr_1fr_1fr_1fr_1fr_auto] gap-4 font-semibold text-gray-700 text-sm">
                <div
                  data-tooltip-id="type-tooltip"
                  data-tooltip-content="Тип нотификации (например, Telegram)"
                  className="cursor-help"
                >
                  Тип
                </div>
                <div
                  data-tooltip-id="condition-tooltip"
                  data-tooltip-content="Условие, при котором срабатывает нотификация (например, D3 == 1)"
                  className="cursor-help"
                >
                  Условие
                </div>
                <div
                  data-tooltip-id="message-template-tooltip"
                  data-tooltip-content="Шаблон сообщения, которое будет отправлено"
                  className="cursor-help"
                >
                  Шаблон сообщения
                </div>
                <div
                  data-tooltip-id="timeout-tooltip"
                  data-tooltip-content="Минимальный интервал между срабатываниями (в секундах)"
                  className="cursor-help"
                >
                  Таймаут
                </div>
                <div
                  data-tooltip-id="reset-after-tooltip"
                  data-tooltip-content="Сбрасывать таймаут, если условие перестает выполняться"
                  className="cursor-help"
                >
                  Сбрасывать таймаут
                </div>
                <div
                  data-tooltip-id="actions-tooltip"
                  data-tooltip-content="Действия: редактировать или удалить триггер"
                  className="cursor-help"
                >
                  Действия
                </div>
              </div>
            </div>

            <div className="max-h-80 overflow-y-auto">
              {elements.length > 0 ? (
                elements.map((element, index) => (
                  <div
                    key={element.id}
                    className="grid grid-cols-[1fr_1fr_1fr_1fr_1fr_auto] items-center gap-4 border-b border-gray-100 py-3 px-2 hover:bg-gray-50 transition-colors"
                  >
                    <div className="text-gray-800 text-sm">{element.type}</div>
                    <div className="text-gray-800 text-sm font-mono">
                      {element.condition}
                    </div>
                    <div className="text-gray-800 text-sm">
                      {element.messageTemplate}
                    </div>
                    <div className="text-gray-800 text-sm">
                      {element.timeout} сек
                    </div>
                    <div className="text-gray-800 text-sm">
                      {element.resetAfter ? 'Да' : 'Нет'}
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleEditElement(index)}
                        className="p-1 text-gray-500 hover:text-blue-600 hover:bg-gray-100 transition-colors"
                        data-tooltip-id="edit-tooltip"
                        data-tooltip-content="Редактировать"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteElement(index)}
                        className="p-1 text-gray-500 hover:text-red-600 hover:bg-gray-100 transition-colors"
                        data-tooltip-id="delete-tooltip"
                        data-tooltip-content="Удалить"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 bg-gray-50">
                  <div className="text-gray-500 text-sm">
                    Нет добавленных триггеров
                  </div>
                  <div className="text-gray-400 text-xs mt-1">
                    Добавьте триггеры для настройки уведомлений
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Form Section */}
        <div className="bg-gray-50 p-4 border border-gray-200">
          <h4 className="text-base font-semibold text-gray-800 mb-3">
            {editingIndex !== null
              ? 'Редактирование триггера'
              : 'Добавление нового триггера'}
          </h4>

          <div className="grid grid-cols-1 lg:grid-cols-6 gap-3 items-end">
            <div>
              <label
                className="block text-sm font-medium text-gray-700 mb-1"
                data-tooltip-id="type-input-tooltip"
                data-tooltip-content="Тип нотификации (например, Telegram)"
              >
                Тип
              </label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="w-full border border-gray-300 p-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="TG">Telegram</option>
              </select>
            </div>

            <div>
              <label
                className="block text-sm font-medium text-gray-700 mb-1"
                data-tooltip-id="condition-input-tooltip"
                data-tooltip-content="Условие, при котором срабатывает нотификация (например, D3 == 1)"
              >
                Условие
              </label>
              <input
                type="text"
                value={condition}
                onChange={(e) => setCondition(e.target.value)}
                placeholder="D3 AND temperature > 20"
                className="w-full border border-gray-300 p-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label
                className="block text-sm font-medium text-gray-700 mb-1"
                data-tooltip-id="message-input-tooltip"
                data-tooltip-content="Шаблон сообщения, которое будет отправлено"
              >
                Шаблон сообщения
              </label>
              <input
                type="text"
                value={messageTemplate}
                onChange={(e) => setMessageTemplate(e.target.value)}
                placeholder="Температура: { temperature }"
                className="w-full border border-gray-300 p-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label
                className="block text-sm font-medium text-gray-700 mb-1"
                data-tooltip-id="timeout-input-tooltip"
                data-tooltip-content="Минимальный интервал между срабатываниями (в секундах)"
              >
                Таймаут (сек)
              </label>
              <input
                type="number"
                value={timeout}
                onChange={(e) => setTimeout(e.target.value)}
                placeholder="5"
                className="w-full border border-gray-300 p-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex items-center gap-2 h-full">
              <label
                className="flex items-center gap-2 text-sm text-gray-700 h-full pt-6"
                data-tooltip-id="reset-input-tooltip"
                data-tooltip-content="Сбрасывать таймаут, если условие перестает выполняться"
              >
                <input
                  type="checkbox"
                  checked={resetAfter}
                  onChange={(e) => setResetAfter(e.target.checked)}
                  className="border-gray-300"
                />
                Сбрасывать таймаут
              </label>
            </div>

            <button
              onClick={handleAddElement}
              className={`w-full p-2 text-white font-medium transition-colors ${
                editingIndex !== null
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {editingIndex !== null ? 'Обновить' : 'Добавить'}
            </button>
          </div>
        </div>
      </div>

      {/* Tooltips */}
      <Tooltip id="type-tooltip" place="top" className="z-50" />
      <Tooltip id="condition-tooltip" place="top" className="z-50" />
      <Tooltip id="message-template-tooltip" place="top" className="z-50" />
      <Tooltip id="timeout-tooltip" place="top" className="z-50" />
      <Tooltip id="reset-after-tooltip" place="top" className="z-50" />
      <Tooltip id="actions-tooltip" place="top" className="z-50" />
      <Tooltip id="edit-tooltip" place="top" className="z-50" />
      <Tooltip id="delete-tooltip" place="top" className="z-50" />
      <Tooltip id="type-input-tooltip" place="top" className="z-50" />
      <Tooltip id="condition-input-tooltip" place="top" className="z-50" />
      <Tooltip id="message-input-tooltip" place="top" className="z-50" />
      <Tooltip id="timeout-input-tooltip" place="top" className="z-50" />
      <Tooltip id="reset-input-tooltip" place="top" className="z-50" />
    </ModalLayout>
  );
};

export default DeviceTrigger;
